/**
 * AuthorizedUseGate — DWMS EMR
 *
 * Presents the clinic's "Authorized Use Notice" (the signed notice bearing the
 * DWMS stamp) to a NON-admin staff member the FIRST time they ever sign in, and
 * blocks the app until they read and accept it. Once accepted, it never shows
 * to that user again.
 *
 * - Admins / super-admins bypass the gate entirely — the notice is aimed at the
 *   staff who sign in for clinical duties.
 * - Acceptance is recorded server-side (user.authorizedUseAcceptedAt) so it is
 *   truly once-per-user across every device and browser — not per machine.
 *   The login / bootstrap payload carries that timestamp; when it is set the
 *   notice is skipped.
 * - Declining signs the user out (mirrors IdleTimeoutGate.doLogout).
 *
 * This is a UX / acknowledgement layer only; server-side middleware remains the
 * authoritative access control.
 */

import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    FormControlLabel,
    Paper,
    Stack,
    Typography,
} from '@mui/material';
import { usePermissions } from '../hooks/usePermissions';
import { apiPost } from '../api/client';
import { logoutRequest } from '../redux/modules/auth/authSlice';
import stampImg from '../assets/images/authorized-use-stamp.png';

// Verbatim text of the signed Authorized Use Notice.
const NOTICE_TEXT =
    "By signing in to the Dr. Wilburg's Medical Services EMR system, you " +
    "acknowledge that your account is for your individual use only and that " +
    "access is authorized solely for legitimate patient care and clinical " +
    "duties while you are physically on the premises of Dr. Wilburg's Medical " +
    "Services. Access from any off-site location or by any unauthorized person " +
    "is strictly prohibited. Any unauthorized access or misuse may result in " +
    "disciplinary action, revocation of system access, and other applicable " +
    "legal or professional consequences.";

// The user's admin status can take a moment to resolve (permissions load
// async). Cap the wait so a slow/failed fetch can never hang the gate.
const ROLE_RESOLVE_TIMEOUT_MS = 5000;

export default function AuthorizedUseGate({ children }) {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { userData } = useSelector((s) => s.auth);
    const { isAdmin, permissionsLoaded } = usePermissions();

    // Server-recorded first-acceptance timestamp is the source of truth.
    const [accepted, setAccepted] = useState(!!userData?.authorizedUseAcceptedAt);
    const [checked, setChecked] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [roleResolveTimedOut, setRoleResolveTimedOut] = useState(false);

    // Re-sync when the identity or acceptance state changes (e.g. a different
    // user signs in on the same tab after a logout).
    useEffect(() => {
        setAccepted(!!userData?.authorizedUseAcceptedAt);
        setChecked(false);
    }, [userData?.id, userData?.authorizedUseAcceptedAt]);

    useEffect(() => {
        const t = setTimeout(() => setRoleResolveTimedOut(true), ROLE_RESOLVE_TIMEOUT_MS);
        return () => clearTimeout(t);
    }, []);

    const accept = useCallback(async () => {
        setSubmitting(true);
        try {
            await apiPost('auth/accept-authorized-use', {});
        } catch {
            // The gate is an acknowledgement layer, not hard security. If the
            // record fails we still let them work now; because nothing was
            // persisted, they'll simply be asked again next sign-in.
            toast.warn(
                "We couldn't save your acknowledgement — you may be asked again next time you sign in."
            );
        } finally {
            setSubmitting(false);
            setAccepted(true);
        }
    }, []);

    const declineAndSignOut = useCallback(async () => {
        try { await apiPost('auth/logout', {}); } catch { /* ignore */ }
        try { dispatch(logoutRequest()); } catch { /* ignore */ }
        navigate('/login');
    }, [dispatch, navigate]);

    // Admins never see the notice; anyone who has already accepted passes
    // straight through.
    if (isAdmin || accepted) return children;

    // Wait until admin status is known before deciding, so a super-admin whose
    // role isn't literally "admin" doesn't briefly flash the notice.
    if (!permissionsLoaded && !roleResolveTimedOut) {
        return (
            <Box
                sx={{
                    minHeight: '100vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'background.default',
                }}
            >
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: { xs: 2, sm: 4 },
                bgcolor: 'background.default',
            }}
        >
            <Paper
                elevation={3}
                sx={{
                    width: '100%',
                    maxWidth: 640,
                    borderRadius: 3,
                    p: { xs: 3, sm: 5 },
                    textAlign: 'center',
                }}
            >
                <Typography
                    variant="overline"
                    sx={{ letterSpacing: 1.5, color: 'text.secondary' }}
                >
                    Dr. Wilburg&apos;s Medical Services
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.5, mb: 3 }}>
                    Authorized Use Notice
                </Typography>

                <Typography
                    variant="body1"
                    sx={{
                        color: 'text.secondary',
                        textAlign: 'left',
                        lineHeight: 1.75,
                        mb: 3,
                    }}
                >
                    {NOTICE_TEXT}
                </Typography>

                <Box
                    component="img"
                    src={stampImg}
                    alt="Dr. Wilburg's Medical Services official stamp"
                    sx={{
                        display: 'block',
                        width: '100%',
                        maxWidth: 300,
                        height: 'auto',
                        mx: 'auto',
                        mb: 3,
                    }}
                />

                <FormControlLabel
                    sx={{ display: 'flex', justifyContent: 'center', mb: 2, mx: 0 }}
                    control={
                        <Checkbox
                            checked={checked}
                            onChange={(e) => setChecked(e.target.checked)}
                        />
                    }
                    label="I have read, understood, and accept this notice."
                />

                <Stack spacing={1.5}>
                    <Button
                        variant="contained"
                        size="large"
                        disabled={!checked || submitting}
                        onClick={accept}
                        sx={{ borderRadius: 2, py: 1.25, fontWeight: 600 }}
                    >
                        {submitting && (
                            <CircularProgress size={18} color="inherit" sx={{ mr: 1 }} />
                        )}
                        Acknowledge &amp; Continue
                    </Button>
                    <Button
                        variant="text"
                        onClick={declineAndSignOut}
                        disabled={submitting}
                        sx={{ color: 'text.secondary' }}
                    >
                        Decline &amp; sign out
                    </Button>
                </Stack>
            </Paper>
        </Box>
    );
}
